import { useQuery } from "@tanstack/react-query";
import { useDraftMode } from "../contexts/DraftModeContext";
import { backendService } from "../services/backend.service";
import type { DayExpensesResponse } from "../utils/models";
import { useDraftDayStats } from "./useDraftDayStats";

type UseDayExpensesResult = {
	data: DayExpensesResponse | undefined;
	isLoading: boolean;
	error: unknown;
};

export const useDayExpenses = (departmentId: string, date: string, isActual: boolean, enabled: boolean): UseDayExpensesResult => {
	const { isDraftMode } = useDraftMode();
	// In draft, dayExpenses comes from the live-stats payload — `wage_breakdown_rows` is stale.
	// Pass `enabled` through so a collapsed/wage-hidden caller doesn't trigger a network round-trip.
	const draftStats = useDraftDayStats(isDraftMode ? date : undefined, enabled);

	const liveQuery = useQuery<DayExpensesResponse>({
		queryKey: ["day-expenses", departmentId, date, isActual],
		queryFn: async () => {
			const { data } = await backendService.getDayExpenses(departmentId, date, isActual);
			return data;
		},
		enabled: !isDraftMode && enabled && !!departmentId && !!date,
	});

	if (isDraftMode) {
		return {
			data: draftStats.data?.dayExpenses,
			isLoading: draftStats.isLoading,
			error: draftStats.error,
		};
	}
	return { data: liveQuery.data, isLoading: liveQuery.isLoading, error: liveQuery.error };
};
